import { useState, type FormEvent } from "react";

export default function SubmitCTA() {
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("P图痕迹");
  const [desc, setDesc] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!desc.trim()) {
      setError("请先填写照片描述或来源链接。");
      return;
    }
    if (desc.length > 500) {
      setError("描述最多 500 字，请精简后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("请填写有效的接收邮箱，方便回传鉴定结论。");
      return;
    }

    setDone(true);
  };

  return (
    <section id="submit" className="relative w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
      <div className="relative w-full rounded-lg bg-primary-500 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(620px 380px at 12% 0%, oklch(var(--accent-500) / 0.35), transparent 60%)",
          }}
          aria-hidden="true"
        ></div>

        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 p-6 md:p-10 lg:p-12">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/15 text-background-50 font-label text-xs backdrop-blur">
              <i className="ri-shield-star-line"></i>
              开放投稿 · 免费鉴定
            </span>
            <h2 className="font-heading font-black text-2xl md:text-4xl text-background-50 mt-5 leading-tight">
              把可疑的黑料照片，
              <br className="hidden sm:block" />
              交给鉴定团拆解
            </h2>
            <p className="mt-4 text-sm md:text-base text-primary-50/90 leading-relaxed max-w-lg">
              留下照片来源与疑点描述，鉴定团会按 P图痕迹、光影对比、像素分析、辟谣对照四个维度依次核查，
              输出一份可复核的结论。
            </p>

            <ul className="mt-6 space-y-3">
              {[
                "尽量提供原始文件或未经转发的版本",
                "说明你怀疑的点：光影、边缘还是透视",
                "结论分「实锤 / 伪造 / 存疑」，不夸大不臆断",
              ].map((tip) => (
                <li key={tip} className="flex items-start gap-2.5">
                  <i className="ri-checkbox-circle-fill text-accent-400 mt-0.5"></i>
                  <span className="text-sm text-primary-50/95">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-background-50 p-5 md:p-7 border border-background-200">
            {done ? (
              <div className="text-center py-10">
                <span className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto">
                  <i className="ri-checkbox-circle-fill text-3xl text-accent-600"></i>
                </span>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mt-4">
                  已收到你的鉴定请求
                </h3>
                <p className="text-sm text-foreground-600 mt-2 max-w-sm mx-auto leading-relaxed">
                  鉴定团会先做初步筛查，结论预计在 24 小时内回到你填写的邮箱。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDone(false);
                    setDesc("");
                    setEmail("");
                    setCategory("P图痕迹");
                  }}
                  className="mt-5 text-sm px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再提交一张
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950">
                  提交照片鉴定
                </h3>
                <p className="text-xs text-foreground-500 mt-1.5">
                  演示站点暂不接入真实提交，点击后会给出本地反馈。
                </p>

                <div className="mt-5">
                  <label
                    htmlFor="case-category"
                    className="font-label text-xs text-foreground-600"
                  >
                    怀疑类型
                  </label>
                  <select
                    id="case-category"
                    name="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="mt-2 w-full text-sm rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 cursor-pointer"
                  >
                    <option>P图痕迹</option>
                    <option>光影对比</option>
                    <option>像素分析</option>
                    <option>辟谣对照</option>
                    <option>拿不准，请综合判断</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="case-desc" className="font-label text-xs text-foreground-600">
                    照片描述 / 来源链接
                  </label>
                  <textarea
                    id="case-desc"
                    name="description"
                    rows={4}
                    maxLength={500}
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="描述照片内容、流传渠道，以及你觉得可疑的地方…"
                    className="mt-2 w-full text-sm rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 resize-none"
                  ></textarea>
                  <div className="text-right text-[11px] text-foreground-400 mt-1">
                    {desc.length} / 500
                  </div>
                </div>

                <div className="mt-2">
                  <label htmlFor="case-email" className="font-label text-xs text-foreground-600">
                    接收邮箱
                  </label>
                  <input
                    id="case-email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="mt-2 w-full text-sm rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-field-note"
                />

                {error && (
                  <p className="mt-4 text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-fill"></i>
                  <span className="text-sm font-medium">提交鉴定请求</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}